import axios from "axios";

/** What the backend's `ErrorResponse` (`{ serviceId, errorCode, errorValues, message }`) tells us about a failed call. */
export interface ApiError {
  status: number | undefined;
  errorCode: number | undefined;
  /** `errorValues.reason`, the backend's explanation for validation errors. */
  reason: string | undefined;
  /** `errorValues.field`, the request field a validation error is about. */
  field: string | undefined;
}

export const getApiError = (error: unknown): ApiError => {
  const response = axios.isAxiosError(error) ? error.response : undefined;
  const body = response?.data;
  const errorCode = body && typeof body === "object" ? Number(body.errorCode) : NaN;
  const reason = body && typeof body === "object" ? body.errorValues?.reason : undefined;
  const field = body && typeof body === "object" ? body.errorValues?.field : undefined;

  return {
    status: response?.status,
    errorCode: Number.isNaN(errorCode) ? undefined : errorCode,
    reason: typeof reason === "string" && reason ? reason : undefined,
    field: typeof field === "string" && field ? field : undefined,
  };
};

// Each code is shared with other errors in its service; only the trade lock answers 403.
const TRADE_LOCK_ERRORS = [
  { serviceId: "CONTRACT", errorCode: 104 },
  { serviceId: "APP", errorCode: 168 },
];

/** The company a trade was refused for because it is trade locked, read from `errorValues.companyId`. */
export const getTradeLockedCompanyId = (error: unknown): number | undefined => {
  const response = axios.isAxiosError(error) ? error.response : undefined;
  const body = response?.data;
  if (response?.status !== 403 || !body || typeof body !== "object") return undefined;

  const isTradeLock = TRADE_LOCK_ERRORS.some(
    ({ serviceId, errorCode }) => body.serviceId === serviceId && Number(body.errorCode) === errorCode
  );
  const companyId: unknown = body.errorValues?.companyId;
  return isTradeLock && Number.isInteger(companyId) ? (companyId as number) : undefined;
};
